@if (loading()) {
  <div class="flex items-center gap-3 p-12 text-sm text-muted-foreground">
    <hlm-spinner size="20" />
    Loading phone service…
  </div>
}

@if (error(); as err) {
  <div class="mx-auto max-w-[800px] p-6">
    <cmt-error-state
      title="Failed to load phone service"
      [message]="err"
      (retry)="reloadPhoneService()" />
  </div>
}

@if (phoneService(); as ps) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- HERO CARD -->
    <div class="mb-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="cmt-eyebrow">
            Phone Service · PS {{ ps.id }}
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            {{ ps.description || 'Phone Service' }}
            <span class="font-normal text-muted-foreground">({{ ps.type || '—' }})</span>
          </h1>
          <p class="text-sm text-muted-foreground">
            {{ getLanguageName(ps.language) }} · {{ ps.timezone || 'No timezone' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Sub-hero actions: cross-entity jumps + portal launch (right-aligned). -->
    @if (!editMode()) {
      <div class="-mt-4 mb-1 flex flex-wrap items-start justify-end gap-2">
        <button hlmBtn variant="outline"
                [disabled]="!canGoToContract()"
                [hlmTooltip]="canGoToContract() ? 'Open the contract for this phone service' : 'No contract linked to this phone service'"
                (click)="goToContract()">
          <hlm-icon name="lucideFileText" size="0.9rem" />
          Go to Contract
        </button>
        <button hlmBtn variant="outline" (click)="openCallViewer()">
          <hlm-icon name="lucidePhone" size="0.9rem" />
          Open Call Viewer
        </button>
        <div class="mx-1 h-6 w-px bg-border" aria-hidden="true"></div>
        <button hlmBtn variant="default" (click)="openCai()">
          <hlm-icon name="lucideExternalLink" size="0.9rem" />
          Open CAI (ext.)
        </button>
      </div>
    }

    <!-- Two-column: General + Greeting & Call Handling -->
    <div class="grid gap-5 lg:grid-cols-2">

      <!-- General -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideInfo" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">General</h2>
        </header>

        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">ID</dt>
            <dd class="font-mono-feat text-sm text-foreground">{{ ps.id }}</dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Description</dt>
            <dd class="text-sm font-medium text-foreground">{{ ps.description || '—' }}</dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Type</dt>
            <dd class="text-sm font-medium text-foreground">{{ ps.type || '—' }}</dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Language</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <div class="ml-auto max-w-[200px]">
                  <hlm-select
                    [options]="languageOptions()"
                    [value]="form.controls.language.value"
                    (valueChange)="form.controls.language.setValue($any($event) || 0)"
                  />
                </div>
              } @else {
                <span class="text-sm font-medium text-foreground">{{ getLanguageName(ps.language) }}</span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Country Prefix</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <div class="relative ml-auto w-28">
                  <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 font-mono-feat text-sm text-muted-foreground">+</span>
                  <input
                    hlmInput
                    size="sm"
                    inputmode="numeric"
                    class="pl-5 text-left font-mono-feat"
                    [formControl]="form.controls.countryPrefix"
                  />
                </div>
                @if (form.controls.countryPrefix.invalid && form.controls.countryPrefix.touched) {
                  <p class="mt-1 text-right text-sm text-destructive">Prefix must be numeric</p>
                }
              } @else {
                <span [class]="'font-mono-feat text-sm ' + (ps.countryPrefix ? 'text-foreground' : 'text-muted-foreground/60')">
                  {{ ps.countryPrefix ? '+' + ps.countryPrefix : '—' }}
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Timezone</dt>
            <dd class="text-sm font-medium text-foreground">{{ ps.timezone || '—' }}</dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Sub Account Info</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.subAccountInfo" />
              } @else {
                <span [class]="'text-sm font-medium ' + (ps.subAccountInfo ? 'text-foreground' : 'text-muted-foreground/60')">
                  {{ ps.subAccountInfo || '—' }}
                </span>
              }
            </dd>
          </div>
        </dl>
      </section>

      <!-- Greeting & Call Handling -->
      <div class="flex flex-col gap-5">

        <!-- Greeting -->
        <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <header class="mb-4 flex items-center gap-2.5">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-accent/20 text-accent-foreground">
              <hlm-icon name="lucideMessageSquare" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-semibold text-foreground">Greeting</h2>
          </header>

          @if (editMode()) {
            <textarea hlmTextarea [formControl]="form.controls.greeting" rows="3"
              class="w-full"></textarea>
          } @else {
            <div class="rounded-lg border border-border/50 bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
              @for (seg of greetingSegments(); track seg.value) {
                @if (seg.isPlaceholder) {
                  <span class="rounded bg-primary/10 px-1 py-0.5 font-mono-feat text-sm text-primary">{{ seg.value }}</span>
                } @else {
                  {{ seg.value }}
                }
              }
            </div>
          }
        </section>

        <!-- Call Handling -->
        <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <header class="mb-4 flex items-center gap-2.5">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <hlm-icon name="lucidePhoneIncoming" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-semibold text-foreground">Call Handling</h2>
          </header>

          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Callnote Subject</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input hlmInput size="sm" class="text-left" [formControl]="form.controls.callnoteSubject" />
                } @else {
                  <span [class]="'text-sm font-medium ' + (ps.callnoteSubject ? 'text-foreground' : 'text-muted-foreground/60')">
                    {{ ps.callnoteSubject || '—' }}
                  </span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Forwarding Type</dt>
              <dd class="text-sm font-medium text-foreground">{{ ps.callnoteForwardingType || '—' }}</dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Call Group</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <div class="ml-auto max-w-[200px]">
                    <!-- Disabled while the call-group list is unknown: the record's own
                         value is still shown, but nothing can be picked against a list we
                         do not have — and "None" would otherwise be the only option
                         (PR #42 re-review defect 2). -->
                    <hlm-select
                      [options]="callGroupOptions()"
                      [value]="form.controls.callGroupId.value"
                      [disabled]="referenceDataFailed()"
                      (valueChange)="form.controls.callGroupId.setValue($any($event))"
                    />
                  </div>
                } @else {
                  <span [class]="'text-sm font-medium ' + (ps.callGroup ? 'text-foreground' : 'text-muted-foreground/60')">
                    {{ ps.callGroup?.name || '—' }}
                  </span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Admit Outsourcing</dt>
              <dd>
                @if (editMode()) {
                  <hlm-switch [formControl]="form.controls.admitOutsourcing" />
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ boolDisplay(ps.admitOutsourcing) }}</span>
                }
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </div>

    <!-- Features & Settings (full width) -->
    <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideSlidersHorizontal" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Features &amp; Settings</h2>
      </header>

      <div class="grid gap-5 lg:grid-cols-2">

        <!-- Call Settings group -->
        <div>
          <p class="mb-2 cmt-eyebrow">Call Settings</p>
          <div class="flex flex-wrap gap-2">
            <!-- Block Clip -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.blockClip" />
                <span class="text-sm text-foreground">Block Clip</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['blockClip']">
                Block Clip
                <span class="font-mono-feat opacity-70">{{ ps.blockClip === null ? 'Not set' : ps.blockClip ? 'Hidden' : 'Visible' }}</span>
              </hlm-badge>
            }

            <!-- Caller Number Visible -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.callerNumberVisible" />
                <span class="text-sm text-foreground">Caller ID in callnotes</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['callerNumberVisible']">
                Caller ID in callnotes
                <span class="font-mono-feat opacity-70">{{ boolDisplay(ps.callerNumberVisible) }}</span>
              </hlm-badge>
            }

            <!-- Show All Email Recipients -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.showAllEmailRecipients" />
                <span class="text-sm text-foreground">Show All Email Recipients</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['showAllEmailRecipients']">
                Show All Email Recipients
                <span class="font-mono-feat opacity-70">{{ boolDisplay(ps.showAllEmailRecipients) }}</span>
              </hlm-badge>
            }

            <!-- Holidays As Sunday -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <span class="text-sm text-foreground">Holidays As Sunday</span>
                <div class="max-w-[150px]">
                  <hlm-select
                    [options]="holidayAsSundayOptions"
                    [formControl]="form.controls.holidayAsSunday"
                  />
                </div>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['holidayAsSunday']">
                Holidays As Sunday
                <span class="font-mono-feat opacity-70">{{
                  ps.holidayAsSunday === null ? 'Brand default' : ps.holidayAsSunday ? 'Yes' : 'No'
                }}</span>
              </hlm-badge>
            }
          </div>
        </div>

        <!-- SMS & Fax group -->
        <div>
          <p class="mb-2 cmt-eyebrow">SMS &amp; Fax</p>
          <div class="flex flex-wrap gap-2">
            <!-- Multipart SMS -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.multipartSms" />
                <span class="text-sm text-foreground">Multipart SMS</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['multipartSms']">
                Multipart SMS
                <span class="font-mono-feat opacity-70">{{ boolDisplay(ps.multipartSms) }}</span>
              </hlm-badge>
            }

            <!-- SMS Sender Name -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <span class="text-sm text-foreground">SMS Sender</span>
                <input hlmInput size="sm" class="max-w-[140px]" [formControl]="form.controls.smsSenderName" />
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['smsSenderName']">
                SMS Sender
                <span class="font-mono-feat opacity-70">{{ ps.smsSenderName || '—' }}</span>
              </hlm-badge>
            }

            <!-- Fax Handling -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <span class="text-sm text-foreground">Fax Handling</span>
                <div class="max-w-[140px]">
                  <hlm-select
                    [options]="faxHandlingOptions"
                    [value]="form.controls.faxHandling.value"
                    (valueChange)="form.controls.faxHandling.setValue($any($event) || '')"
                  />
                </div>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['faxHandling']">
                Fax Handling
                <span class="font-mono-feat opacity-70">{{ ps.faxHandling || '—' }}</span>
              </hlm-badge>
            }
          </div>
        </div>

        <!-- Recording & Transfer group -->
        <div>
          <p class="mb-2 cmt-eyebrow">Recording &amp; Transfer</p>
          <div class="flex flex-wrap gap-2">
            <!-- Call Recording -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.callRecordingAllowed" />
                <span class="text-sm text-foreground">Call Recording</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['callRecordingAllowed']">
                Call Recording
                <span class="font-mono-feat opacity-70">{{ boolDisplay(ps.callRecordingAllowed) }}</span>
              </hlm-badge>
            }

            <!-- Transcription -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <hlm-switch [formControl]="form.controls.transcriptionAllowed" />
                <span class="text-sm text-foreground">Transcription</span>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['transcriptionAllowed']">
                Transcription
                <span class="font-mono-feat opacity-70">{{ boolDisplay(ps.transcriptionAllowed) }}</span>
              </hlm-badge>
            }

            <!-- Blind Transfer -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <span class="text-sm text-foreground">Blind Transfer</span>
                <div class="max-w-[140px]">
                  <hlm-select
                    [options]="blindTransferOptions"
                    [value]="form.controls.blindTransfer.value"
                    (valueChange)="form.controls.blindTransfer.setValue($any($event) || '')"
                  />
                </div>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['blindTransfer']">
                Blind Transfer
                <span class="font-mono-feat opacity-70">{{ ps.blindTransfer || '—' }}</span>
              </hlm-badge>
            }

            <!-- Voicebot Mode -->
            @if (editMode()) {
              <div class="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
                <span class="text-sm text-foreground">Voicebot Mode</span>
                <div class="max-w-[160px]">
                  <!-- Same guard as the call group above (defect 2). -->
                  <hlm-select
                    [options]="voicebotModeOptions()"
                    [value]="form.controls.voiceBotMode.value"
                    [disabled]="referenceDataFailed()"
                    (valueChange)="form.controls.voiceBotMode.setValue($any($event))"
                  />
                </div>
              </div>
            } @else {
              <hlm-badge [variant]="chipVariants()['voiceBotMode']">
                Voicebot Mode
                <span class="font-mono-feat opacity-70">{{ ps.voiceBotMode || '—' }}</span>
              </hlm-badge>
            }
          </div>
        </div>
      </div>
    </section>

    <!-- Outbound: own number on outbound calls (PS-6598), outgoing number, outbound greeting -->
    @if (showOutboundSection()) {
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="ps-outbound-section">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucidePhoneOutgoing" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Outbound</h2>
        </header>

        <!-- Stays visible in edit mode so the Edit/Save row below does not move; sending is disabled then. -->
        @if (outboundReadiness(); as r) {
          @if (r.applies) {
          <div class="mb-5 border-b border-border/70 pb-5" data-testid="ps-outbound-readiness">
            <div class="mb-2 flex items-center gap-2">
              <h3 class="cmt-eyebrow">Own number on outbound calls</h3>
              <cmt-help-icon label="Own number on outbound calls" [text]="readinessHelp" />
              <ng-template #readinessHelp>
                <p class="max-w-xs text-sm">In Germany the own number is shown on outbound calls only with a SIP account (contract extension + SIP number) and a verified number.</p>
              </ng-template>
            </div>

            <div class="grid gap-5 lg:grid-cols-2">
              <!-- Label-value rows (Style Guide §6.3). No links: the SIP order link books extension and SIP number together. -->
              <dl class="flex flex-col divide-y divide-border/70">
                <div class="flex items-center justify-between gap-4 py-2.5">
                  <dt class="cmt-eyebrow">Outbound Phone Number</dt>
                  <dd class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-right">
                    @if (editMode()) {
                      <input hlmInput size="sm" class="max-w-[180px] text-right font-mono-feat" aria-label="Outbound phone number" [formControl]="form.controls.outboundOutgoingNumber"
                             data-testid="ps-outbound-number-input" />
                    } @else {
                      <span [class]="'font-mono-feat text-base ' + (ps.outboundOutgoingNumber ? 'text-foreground' : 'text-muted-foreground/60')">
                        {{ ps.outboundOutgoingNumber || '—' }}
                      </span>
                    }
                    @if (numberBadge(); as b) {
                      <hlm-badge [variant]="b.variant" data-testid="ps-outbound-number-status">{{ b.label }}</hlm-badge>
                    }
                    <cmt-help-icon label="Outbound number verification" data-testid="ps-outbound-number-help"
                      text="For now, a number saved here counts as verified straight away (grandfathered). This is a temporary workaround." />
                    @if (editMode() && form.controls.outboundOutgoingNumber.invalid && form.controls.outboundOutgoingNumber.touched) {
                      <p class="basis-full text-right text-sm text-destructive">Not a dialable number</p>
                    }
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-2.5">
                  <dt class="cmt-eyebrow">SIP Account</dt>
                  <dd class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-right">
                    @if (extensionBadge(); as b) {
                      <hlm-badge [variant]="b.variant" data-testid="ps-sip-account-status">{{ b.label }}</hlm-badge>
                    } @else {
                      <hlm-spinner size="16" />
                    }
                    @if (r.extensionFee !== null) {
                      <span class="font-mono-feat text-base text-muted-foreground">{{ r.extensionFee.toFixed(2) }} {{ currencySymbol() }} / month</span>
                    }
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-4 py-2.5">
                  <dt class="cmt-eyebrow">SIP Number</dt>
                  <dd class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-right">
                    @if (sipNumberBadge(); as b) {
                      <hlm-badge [variant]="b.variant" data-testid="ps-sip-number-status">{{ b.label }}</hlm-badge>
                    } @else {
                      <hlm-spinner size="16" />
                    }
                    @if (r.sipNumberValue) {
                      <span class="font-mono-feat text-base text-foreground">{{ r.sipNumberValue }}</span>
                    }
                  </dd>
                </div>
              </dl>

              <div class="min-w-0">
                @if (cliStatusSettled() && prerequisitesSettled() && userAccount(); as ua) {
                  <cmt-sip-order-link-section [userAccount]="ua"
                    [notNeededReason]="sipOrderLinkNotNeeded()"
                    [blockedReason]="sipOrderLinkBlocked()"
                    [books]="sipOrderLinkBooks()"
                    (openUserAccount)="goToUserAccount()" />
                } @else if (userAccountLoadFailed()) {
                  <hlm-alert tone="error" class="items-center" data-testid="ps-sip-order-link-account-error">
                    <hlm-icon name="lucideCircleAlert" size="1rem" class="text-destructive" />
                    <span class="flex-1">The user account could not be loaded, so the SIP order link cannot be sent.</span>
                    <button type="button" hlmBtn variant="outline" size="sm" (click)="retryUserAccount()"
                            data-testid="ps-sip-order-link-account-retry">
                      <hlm-icon name="lucideRefreshCw" size="0.9rem" />
                      Retry
                    </button>
                  </hlm-alert>
                } @else {
                  <div class="flex items-center gap-3 py-2 text-sm text-muted-foreground">
                    <hlm-spinner size="18" />
                    Checking the SIP account…
                  </div>
                }
              </div>
            </div>
          </div>
          }
        }

        <div class="grid gap-5 lg:grid-cols-2">
          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Outgoing Phone Number</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input hlmInput size="sm" class="ml-auto max-w-[180px] text-right font-mono-feat" aria-label="Outgoing phone number" [formControl]="form.controls.outgoingPhoneNumber" />
                  @if (form.controls.outgoingPhoneNumber.invalid) {
                    <p class="mt-1 text-right text-sm text-destructive">Whitespaces are not allowed</p>
                  }
                } @else {
                  <span [class]="'font-mono-feat text-base ' + (ps.outgoingPhoneNumber ? 'text-foreground' : 'text-muted-foreground/60')">
                    {{ ps.outgoingPhoneNumber || '—' }}
                  </span>
                }
              </dd>
            </div>
          </dl>

          <!-- Outbound greeting: same body as the Greeting card (boxed prose with placeholder chips; textarea in edit mode). -->
          <div class="flex flex-col gap-2 py-2.5">
            <label for="ps-outbound-greeting-input" class="cmt-eyebrow">Outbound Greeting</label>
            @if (editMode()) {
              <textarea hlmTextarea id="ps-outbound-greeting-input" [formControl]="form.controls.outboundGreeting" rows="3" class="w-full"
                        data-testid="ps-outbound-greeting-input"></textarea>
            } @else {
              <div class="min-h-[4.25rem] rounded-lg border border-border/50 bg-muted/50 p-3 text-sm leading-relaxed text-foreground"
                   data-testid="ps-outbound-greeting">
                @if (ps.outboundGreeting) {
                  @for (seg of outboundGreetingSegments(); track $index) {
                    @if (seg.isPlaceholder) {
                      <span class="rounded bg-primary/10 px-1 py-0.5 font-mono-feat text-sm text-primary">{{ seg.value }}</span>
                    } @else {
                      {{ seg.value }}
                    }
                  }
                } @else {
                  <span class="text-muted-foreground">No outbound greeting set.</span>
                }
              </div>
            }
          </div>
        </div>
      </section>
    }

    <!-- Waiting Queue: inbound calls waiting for an agent; its own card since it has nothing to do with outbound. -->
    @if (showWaitingQueueSection()) {
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="ps-waiting-queue-section">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideClock" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Waiting Queue</h2>
        </header>

        <div class="grid gap-x-8 lg:grid-cols-2">
          @for (column of wqColumns; track $index) {
            <dl class="flex flex-col divide-y divide-border/70">
              @for (wq of column; track wq.key) {
                <div class="flex items-center justify-between gap-4 py-2.5">
                  <dt class="flex items-center gap-2">
                    <span class="cmt-eyebrow">{{ wq.label }}</span>
                    <cmt-help-icon [label]="wq.label" [text]="wq.help" />
                  </dt>
                  <dd class="min-w-0 flex-1 text-right">
                    @switch (wq.key) {
                      @case ('priority') {
                        @if (editMode()) {
                          <!-- Invisible unit keeps the input edge in line with the "sec" rows. -->
                          <div class="ml-auto flex max-w-[140px] items-center gap-1">
                            <input hlmInput size="sm" type="number" class="text-right" [attr.aria-label]="wq.label" [formControl]="form.controls.waitingQueuePriority" />
                            <span class="invisible text-sm" aria-hidden="true">sec</span>
                          </div>
                        } @else {
                          <span [class]="'font-mono-feat text-base ' + (ps.waitingQueuePriority !== null ? 'text-foreground' : 'text-muted-foreground/60')">
                            {{ ps.waitingQueuePriority ?? '—' }}
                          </span>
                        }
                      }
                      @case ('ringTime') {
                        @if (editMode()) {
                          <div class="ml-auto flex max-w-[140px] items-center gap-1">
                            <input hlmInput size="sm" type="number" class="text-right" [attr.aria-label]="wq.label" [formControl]="form.controls.waitingQueueRingTime" />
                            <span class="text-sm text-muted-foreground">sec</span>
                          </div>
                        } @else {
                          <span class="font-mono-feat text-base text-foreground">{{ ps.waitingQueueRingTime }}s</span>
                        }
                      }
                      @case ('timeout') {
                        @if (editMode()) {
                          <div class="ml-auto flex max-w-[140px] items-center gap-1">
                            <input hlmInput size="sm" type="number" min="5" max="3600" class="text-right" [attr.aria-label]="wq.label" [formControl]="form.controls.waitingQueueTimeout" />
                            <span class="text-sm text-muted-foreground">sec</span>
                          </div>
                          @if (form.controls.waitingQueueTimeout.invalid && form.controls.waitingQueueTimeout.touched) {
                            <p class="mt-1 text-right text-sm text-destructive">Must be between 5 and 3600 seconds, or empty</p>
                          }
                        } @else {
                          <span [class]="'font-mono-feat text-base ' + (ps.waitingQueueTimeout !== null ? 'text-foreground' : 'text-muted-foreground/60')">
                            {{ ps.waitingQueueTimeout !== null ? ps.waitingQueueTimeout + 's' : '—' }}
                          </span>
                        }
                      }
                      @case ('forwardTo') {
                        @if (editMode()) {
                          <div class="ml-auto flex max-w-[180px] items-center gap-1">
                            <input hlmInput size="sm" type="text" maxlength="30" class="text-right font-mono-feat" [attr.aria-label]="wq.label" [formControl]="form.controls.waitingQueueTimeoutForwardTo" />
                            <span class="invisible text-sm" aria-hidden="true">sec</span>
                          </div>
                          @if (form.controls.waitingQueueTimeoutForwardTo.invalid && form.controls.waitingQueueTimeoutForwardTo.touched) {
                            <p class="mt-1 text-right text-sm text-destructive">Not a dialable number (max 30 characters)</p>
                          }
                        } @else {
                          <span [class]="'font-mono-feat text-base ' + (ps.waitingQueueTimeoutForwardTo ? 'text-foreground' : 'text-muted-foreground/60')">
                            {{ ps.waitingQueueTimeoutForwardTo || '—' }}
                          </span>
                        }
                      }
                    }
                  </dd>
                </div>
              }
            </dl>
          }
        </div>
      </section>
    }

    <!-- PS Actions -->
    <div class="flex flex-wrap items-center gap-2">
      @if (!editMode()) {
        <button hlmBtn variant="outline" (click)="startEdit()">
          <hlm-icon name="lucidePencil" size="0.9rem" />
          Edit
        </button>
      } @else {
        <button hlmBtn variant="default" (click)="save()" [disabled]="form.invalid || saving()">
          @if (saving()) {
            <hlm-spinner size="16" />
            Saving…
          } @else {
            <hlm-icon name="lucideSave" size="0.9rem" />
            Save
          }
        </button>
        <button hlmBtn variant="outline" (click)="cancelEdit()">Cancel</button>
      }
    </div>

    <!-- Skills section. `min-w-0` is load-bearing: as a flex item in a column the
         section defaults to min-width:auto, which makes it grow to its widest child
         (the 810px skills track) and push the whole page into a horizontal scrollbar
         instead of letting the track scroll inside it (EB-4170 H7). -->
    <section class="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-accent/20 text-accent-foreground">
          <hlm-icon name="lucideSparkles" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Skills</h2>
      </header>

      @if (skillsLoading()) {
        <div class="flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <hlm-spinner size="16" /> Loading skills…
        </div>
      } @else {
        <!-- The column track has a fixed 810px floor: 770px of columns plus 5 × gap-2 (the
             time inputs need room for hh:mm:ss plus their picker). It scrolls inside the
             card instead of pushing the page into a horizontal scrollbar on narrower
             desktops (EB-4170 H7). -->
        <div class="overflow-x-auto">
          <div class="min-w-[810px]">
          <!-- Skills table header -->
          <!-- cmt-eyebrow, not a hand-rolled copy: the inlined version had drifted to
               tracking 0.16em and dropped font-semibold, so it did not match the eyebrow
               labels elsewhere on this page (Style Guide §4.3 / §16 #9; review roundup #15). -->
          <div class="cmt-eyebrow grid-cols-[minmax(160px,1.5fr)_150px_120px_120px_minmax(140px,1fr)_80px] mb-1 hidden items-center gap-2 border-b border-border/70 pb-2 md:grid">
            <span>Skill</span>
            <span>Required?</span>
            <span>Start</span>
            <span>End</span>
            <span>Weekdays</span>
            <span></span>
          </div>

          <!-- Skill rows -->
          @for (s of skills(); track s.skill.name) {
            @if (editingSkillName() === s.skill.name) {
              <!-- Inline edit row -->
              <div class="grid-cols-[minmax(160px,1.5fr)_150px_120px_120px_minmax(140px,1fr)_80px] grid items-center gap-2 border-b border-border/50 py-2">
                <span class="text-sm font-medium text-foreground">{{ s.skill.name }}</span>
                <!-- ON = "Must have", answering the column's question. The visible label
                     carries the state; the accessible name is static, because a name that
                     flips with the state announces "Nice to have, switch, not checked" —
                     the double negative EB-4172 set out to remove (review roundup #14). -->
                <hlm-switch [(checked)]="skillFormRequired"
                            ariaLabel="Required"
                            [label]="skillFormRequired ? 'Must have' : 'Nice to have'" />
                <span>
                  <input hlmInput size="sm" type="time" step="1" [(ngModel)]="skillFormStartTime" />
                </span>
                <span>
                  <input hlmInput size="sm" type="time" step="1" [(ngModel)]="skillFormEndTime" />
                </span>
                <span>
                  <div class="flex gap-1">
                    @for (day of weekdayLabels; track day.short; let i = $index) {
                      <button
                        type="button"
                        [class]="'grid h-7 w-7 place-items-center rounded-md text-sm font-medium transition-colors cursor-pointer ' +
                          (skillFormWeekdays[i]
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80')"
                        (click)="toggleSkillWeekday(i)">
                        {{ day.short }}
                      </button>
                    }
                  </div>
                </span>
                <span class="flex items-center gap-1">
                  <button hlmBtn variant="default" size="icon" (click)="saveEditedSkill()" [disabled]="skillSaving()">
                    <hlm-icon name="lucideCheck" size="0.9rem" />
                  </button>
                  <button hlmBtn variant="ghost" size="icon" (click)="cancelSkillEdit()">
                    <hlm-icon name="lucideX" size="0.9rem" />
                  </button>
                </span>
              </div>
            } @else {
              <!-- Read-only row -->
              <div class="grid-cols-[minmax(160px,1.5fr)_150px_120px_120px_minmax(140px,1fr)_80px] grid items-center gap-2 border-b border-border/50 py-2">
                <span class="text-sm font-medium text-foreground">{{ s.skill.name }}</span>
                <span>
                  <hlm-badge [variant]="s.nice ? 'accent' : 'default'" class="text-sm">
                    {{ s.nice ? 'Nice to have' : 'Must have' }}
                  </hlm-badge>
                </span>
                <span class="font-mono-feat text-sm text-foreground">{{ formatTime(s.startTime) }}</span>
                <span class="font-mono-feat text-sm text-foreground">{{ formatTime(s.endTime) }}</span>
                <span class="text-sm text-foreground">{{ formatWeekdays(s.weekdays) }}</span>
                <span class="flex items-center gap-1">
                  <button hlmBtn variant="ghost" size="icon"
                          (click)="startEditSkill(s)"
                          [disabled]="addingSkill() || editingSkillName() !== null">
                    <hlm-icon name="lucidePencil" size="0.9rem" />
                  </button>
                  <!-- outline + a border, not ghost + text-destructive: §16 #11 forbids
                       that pairing on contrast grounds, and cc-numbers.component.html:155
                       already does it this way (review roundup #13). -->
                  <button hlmBtn variant="outline" size="icon"
                          class="border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive hover:text-destructive"
                          (click)="removeSkill(s.skill.name)"
                          [disabled]="addingSkill() || editingSkillName() !== null">
                    <hlm-icon name="lucideTrash2" size="0.9rem" />
                  </button>
                </span>
              </div>
            }
          }

          <!-- Add skill row -->
          @if (addingSkill()) {
            <div class="grid-cols-[minmax(160px,1.5fr)_150px_120px_120px_minmax(140px,1fr)_80px] grid items-center gap-2 border-b border-border/50 py-2">
              <span>
                <hlm-select
                  [options]="addableSkillOptions()"
                  [value]="skillFormName || null"
                  (valueChange)="onSkillSelected($any($event) || '')"
                  placeholder="Select skill…"
                />
              </span>
              <!-- Static accessible name, stateful visible label — see the edit row above
                   (review roundup #14). -->
              <hlm-switch [(checked)]="skillFormRequired"
                          ariaLabel="Required"
                          [label]="skillFormRequired ? 'Must have' : 'Nice to have'" />
              <span>
                <input hlmInput size="sm" type="time" step="1" [(ngModel)]="skillFormStartTime" />
              </span>
              <span>
                <input hlmInput size="sm" type="time" step="1" [(ngModel)]="skillFormEndTime" />
              </span>
              <span>
                <div class="flex gap-1">
                  @for (day of weekdayLabels; track day.short; let i = $index) {
                    <button
                      type="button"
                      [class]="'grid h-7 w-7 place-items-center rounded-md text-sm font-medium transition-colors cursor-pointer ' +
                        (skillFormWeekdays[i]
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80')"
                      (click)="toggleSkillWeekday(i)">
                      {{ day.short }}
                    </button>
                  }
                </div>
              </span>
              <span class="flex items-center gap-1">
                <button hlmBtn variant="default" size="icon" (click)="saveNewSkill()" [disabled]="!skillFormName || skillSaving()">
                  <hlm-icon name="lucideCheck" size="0.9rem" />
                </button>
                <button hlmBtn variant="ghost" size="icon" (click)="cancelSkillEdit()">
                  <hlm-icon name="lucideX" size="0.9rem" />
                </button>
              </span>
            </div>
          }
          </div>
        </div>

        @if (skills().length === 0 && !addingSkill()) {
          <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
            No skills configured
          </div>
        }

        <!-- Add skill button -->
        <div class="mt-3">
          <button hlmBtn variant="outline" (click)="startAddSkill()" [disabled]="addingSkill() || editingSkillName() !== null">
            <hlm-icon name="lucidePlus" size="0.9rem" />
            Add Skill
          </button>
        </div>
      }
    </section>

  </div>
}
